import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import JobCard from './JobCard'
import JobDetailErrorBoundary from './JobDetailErrorBoundary'
import NewJobModal from './NewJobModal'
import { getBizColor, getStatusColor } from '../../utils/colors'
import { renumberJobs, resetOrderByDate, ACTIVE_STATUSES } from '../../utils/jobOrdering'
import { isCancelled, isCompleted } from '../../utils/jobStatus'
import { pickRelevantSegment, sortSegments } from '../../lib/segments'

const DAY_MS = 86400000

function priorityAccent(job) {
  if (isCancelled(job)) {
    return { color: getStatusColor('cancelled'), label: 'CANCELLED', pulse: false, struck: true }
  }
  if (isCompleted(job)) {
    return { color: '#4a8c6a', label: 'DONE', pulse: false }
  }
  if (!job.start_date) return { color: 'rgba(255,255,255,0.15)', label: 'QUEUED', pulse: false }
  const today = new Date(); today.setHours(0, 0, 0, 0)
  const start = new Date(job.start_date + 'T12:00:00')
  const days = Math.round((start - today) / DAY_MS)
  if (days < 0) return { color: '#e05c5c', label: 'OVERDUE', pulse: true }
  if (days <= 3) return { color: '#e05c5c', label: 'URGENT', pulse: false }
  if (days <= 7) return { color: '#b48c3c', label: 'UPCOMING', pulse: false }
  return { color: 'rgba(255,255,255,0.2)', label: 'QUEUED', pulse: false }
}

function formatDate(iso) {
  if (!iso) return '—'
  const d = new Date(iso + 'T12:00:00')
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
}

function isToday(iso) {
  if (!iso) return false
  const today = new Date(); today.setHours(0, 0, 0, 0)
  return new Date(iso + 'T12:00:00').toDateString() === today.toDateString()
}

export default function JobsPage() {
  const { profile, activeBusiness } = useAuth()
  const [jobs, setJobs] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedJob, setSelectedJob] = useState(null)
  const [showNewJob, setShowNewJob] = useState(false)
  const [tab, setTab] = useState('active')
  const [dateScope, setDateScope] = useState('all')
  const [completedScope, setCompletedScope] = useState('season') // week | month | season | all
  const [cancelledScope, setCancelledScope] = useState('season') // week | month | season | all
  const [sortMode, setSortMode] = useState('priority')
  const [search, setSearch] = useState('')
  const [savingOrder, setSavingOrder] = useState(false)

  const role = profile?.role
  const isOwner = role === 'owner'
  const canEditJobs = role === 'owner' || role === 'office_staff' || role === 'project_manager'
  const canSeeCollected = ['owner', 'office_staff', 'project_manager'].includes(role)

  const fetchJobs = useCallback(async () => {
    setLoading(true)
    let q = supabase.from('jobs')
      .select('*, profiles!jobs_crew_leader_id_fkey(full_name)')
      .order('sort_position', { ascending: true, nullsFirst: false })
      .order('start_date', { ascending: true })
    if (activeBusiness !== 'all') q = q.eq('business_id', activeBusiness)
    if (role === 'laborer') q = q.contains('assigned_laborer_ids', [profile?.id])
    const { data } = await q
    const jobs = data || []
    const segmentedIds = jobs.filter(j => j.has_segments).map(j => j.id)
    if (segmentedIds.length > 0) {
      const { data: segs } = await supabase.from('job_segments').select('*').in('job_id', segmentedIds)
      const byJob = {}
      ;(segs || []).forEach(s => { (byJob[s.job_id] = byJob[s.job_id] || []).push(s) })
      jobs.forEach(j => { j.segments = sortSegments(byJob[j.id] || []) })
    }
    setJobs(jobs)
    setLoading(false)
  }, [activeBusiness, role, profile])

  const location = useLocation()
  useEffect(() => { fetchJobs() }, [fetchJobs, location.key])

  // Three distinct buckets. Cancelled is NOT a kind of completed — it never
  // happened, earned nothing, and must never be counted or filed with finished work.
  const { activeJobs, completedJobs, cancelledJobs } = useMemo(() => {
    const active = []
    const completed = []
    const cancelled = []
    jobs.forEach(j => {
      if (isCancelled(j)) cancelled.push(j)
      else if (isCompleted(j)) completed.push(j)
      else active.push(j)
    })
    return { activeJobs: active, completedJobs: completed, cancelledJobs: cancelled }
  }, [jobs])

  const scopedActive = useMemo(() => {
    if (dateScope === 'all') return activeJobs
    const today = new Date(); today.setHours(0, 0, 0, 0)
    const endWeek = new Date(today); endWeek.setDate(endWeek.getDate() + 7)
    return activeJobs.filter(j => {
      if (!j.start_date) return false
      const d = new Date(j.start_date + 'T12:00:00')
      if (dateScope === 'today') return d.toDateString() === today.toDateString()
      if (dateScope === 'week') return d >= today && d <= endWeek
      return true
    })
  }, [activeJobs, dateScope])

  // Date-scope a terminal list. `anchor` names the column that genuinely dates
  // the outcome: completed_at for finished work, start_date for cancelled work
  // (which has no completion date and must never borrow one).
  const scopeByDate = useCallback((list, scope, anchorOf) => {
    if (scope === 'all') return list
    var now = new Date()
    var weekAgo = new Date(now.getTime() - 7 * 86400000)
    var monthAgo = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())
    var seasonStart = new Date(now.getFullYear(), 0, 1) // YTD = pool season this year
    return list.filter(function(j) {
      var anchor = anchorOf(j)
      // No date to judge by: keep it visible rather than guessing with an
      // unrelated column. 26 completed jobs predate completed_at being written.
      if (!anchor) return true
      var d = new Date(anchor)
      if (scope === 'week') return d >= weekAgo
      if (scope === 'month') return d >= monthAgo
      if (scope === 'season') return d >= seasonStart
      return true
    })
  }, [])

  const scopedCompleted = useMemo(
    () => scopeByDate(completedJobs, completedScope, function(j) { return j.completed_at }),
    [completedJobs, completedScope, scopeByDate]
  )

  const scopedCancelled = useMemo(
    () => scopeByDate(cancelledJobs, cancelledScope, function(j) { return j.start_date }),
    [cancelledJobs, cancelledScope, scopeByDate]
  )

  const completedStats = useMemo(() => {
    var rows = scopedCompleted
    var total = rows.reduce(function(s, j) { return s + (Number(j.amount_collected) || 0) }, 0)
    var withAmount = rows.filter(function(j) { return j.amount_collected != null })
    var avg = withAmount.length > 0 ? total / withAmount.length : 0
    return { count: rows.length, total: total, avg: avg, missing: rows.length - withAmount.length }
  }, [scopedCompleted])

  const cancelledStats = useMemo(() => {
    var lost = scopedCancelled.reduce(function(s, j) { return s + (Number(j.contract_value) || 0) }, 0)
    return { count: scopedCancelled.length, lost: lost }
  }, [scopedCancelled])

  const searched = useMemo(() => {
    const list = tab === 'active' ? scopedActive : tab === 'completed' ? scopedCompleted : scopedCancelled
    if (!search) return list
    const t = search.toLowerCase()
    return list.filter(j => [j.title, j.job_number, j.client_name, j.site_address, j.client_phone, j.client_email, j.status, j.start_date, j.notes].some(f => f && String(f).toLowerCase().includes(t)))
  }, [scopedActive, scopedCompleted, scopedCancelled, tab, search])

  const sorted = useMemo(() => {
    if (tab === 'completed') {
      // Completed list always shows most-recently-completed first.
      return [...searched].sort(function(a, b) {
        var aT = a.completed_at ? new Date(a.completed_at).getTime() : 0
        var bT = b.completed_at ? new Date(b.completed_at).getTime() : 0
        return bT - aT
      })
    }
    if (tab === 'cancelled') {
      // Cancelled jobs have no completion date; order by when they were scheduled.
      return [...searched].sort(function(a, b) {
        return (b.start_date || '').localeCompare(a.start_date || '')
      })
    }
    if (sortMode === 'date') {
      return [...searched].sort((a, b) => (a.start_date || '9999').localeCompare(b.start_date || '9999'))
    }
    if (sortMode === 'customer') {
      return [...searched].sort((a, b) => (a.client_name || '').localeCompare(b.client_name || ''))
    }
    return searched
  }, [searched, sortMode, tab])

  const canDrag = canEditJobs && tab === 'active' && sortMode === 'priority' && !search && dateScope === 'all'

  const handleReorder = useCallback(async (fromIdx, toIdx) => {
    if (fromIdx === toIdx || fromIdx == null || toIdx == null) return
    const reordered = [...sorted]
    const [moved] = reordered.splice(fromIdx, 1)
    reordered.splice(toIdx, 0, moved)
    // Optimistic local update
    const byId = new Map(reordered.map((j, i) => [j.id, i]))
    setJobs(prev => prev.map(j => byId.has(j.id) ? { ...j, sort_position: byId.get(j.id) } : j))
    setSavingOrder(true)
    try { await renumberJobs(reordered) } catch (e) { console.error(e) }
    setSavingOrder(false)
  }, [sorted])

  const handleResetOrder = useCallback(async () => {
    if (!window.confirm('Reset ordering to calendar date? This undoes all manual priority reordering for active jobs.')) return
    setSavingOrder(true)
    try {
      await resetOrderByDate(activeBusiness)
      await fetchJobs()
    } catch (e) { console.error(e) }
    setSavingOrder(false)
  }, [activeBusiness, fetchJobs])

  // --- Drag state ---
  const [dragIdx, setDragIdx] = useState(null)
  const [overIdx, setOverIdx] = useState(null)
  const touchRef = useRef(null)
  const listRef = useRef(null)
  const justDraggedRef = useRef(false)
  const dragStateRef = useRef({ dragIdx: null, overIdx: null })
  useEffect(() => { dragStateRef.current = { dragIdx, overIdx } }, [dragIdx, overIdx])

  // Inject pulse keyframes once
  useEffect(() => {
    const id = 'jobs-page-keyframes'
    if (!document.getElementById(id)) {
      const st = document.createElement('style')
      st.id = id
      st.textContent = '@keyframes jobsOverduePulse { 0%,100% { box-shadow: inset 4px 0 0 #e05c5c, 0 0 0 0 rgba(224,92,92,0); } 50% { box-shadow: inset 4px 0 0 #e05c5c, 0 0 0 6px rgba(224,92,92,0.22); } } .jobs-overdue-pulse { animation: jobsOverduePulse 1.8s ease-in-out infinite; }'
      document.head.appendChild(st)
    }
  }, [])

  // HTML5 drag handlers
  const onDragStart = (idx) => (e) => {
    if (!canDrag) return
    setDragIdx(idx); setOverIdx(idx)
    e.dataTransfer.effectAllowed = 'move'
    try { e.dataTransfer.setData('text/plain', String(idx)) } catch (_) {}
  }
  const onDragOver = (idx) => (e) => {
    if (dragStateRef.current.dragIdx == null) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    if (dragStateRef.current.overIdx !== idx) setOverIdx(idx)
  }
  const onDrop = (idx) => (e) => {
    e.preventDefault()
    const { dragIdx: d } = dragStateRef.current
    if (d != null && d !== idx) handleReorder(d, idx)
    setDragIdx(null); setOverIdx(null)
  }
  const onDragEndHandler = () => { setDragIdx(null); setOverIdx(null) }

  // Touch drag (long-press to initiate)
  const onTouchStart = (idx) => (e) => {
    if (!canDrag) return
    const t = e.touches[0]; if (!t) return
    const state = { idx, startX: t.clientX, startY: t.clientY, active: false, touchId: t.identifier, timer: null }
    state.timer = setTimeout(() => {
      state.active = true
      setDragIdx(idx); setOverIdx(idx)
      if (navigator.vibrate) { try { navigator.vibrate(30) } catch (_) {} }
      document.body.style.userSelect = 'none'
    }, 380)
    touchRef.current = state
  }

  // Non-passive touchmove + touchend to allow preventDefault
  useEffect(() => {
    const el = listRef.current
    if (!el) return
    const move = (e) => {
      const state = touchRef.current
      if (!state) return
      const t = Array.from(e.touches).find(tt => tt.identifier === state.touchId)
      if (!t) return
      if (!state.active) {
        if (Math.abs(t.clientY - state.startY) > 8 || Math.abs(t.clientX - state.startX) > 8) {
          clearTimeout(state.timer); touchRef.current = null
        }
        return
      }
      if (e.cancelable) e.preventDefault()
      const target = document.elementFromPoint(t.clientX, t.clientY)
      const row = target && target.closest && target.closest('[data-job-idx]')
      if (row) {
        const newIdx = parseInt(row.getAttribute('data-job-idx'), 10)
        if (!isNaN(newIdx) && dragStateRef.current.overIdx !== newIdx) setOverIdx(newIdx)
      }
    }
    const end = (e) => {
      const state = touchRef.current
      if (!state) { return }
      if (state.timer) clearTimeout(state.timer)
      const { dragIdx: d, overIdx: o } = dragStateRef.current
      if (state.active) {
        if (e && e.cancelable) e.preventDefault()
        justDraggedRef.current = true
        setTimeout(() => { justDraggedRef.current = false }, 400)
        if (d != null && o != null && d !== o) handleReorder(d, o)
      }
      document.body.style.userSelect = ''
      setDragIdx(null); setOverIdx(null)
      touchRef.current = null
    }
    el.addEventListener('touchmove', move, { passive: false })
    el.addEventListener('touchend', end)
    el.addEventListener('touchcancel', end)
    return () => {
      el.removeEventListener('touchmove', move)
      el.removeEventListener('touchend', end)
      el.removeEventListener('touchcancel', end)
    }
  }, [handleReorder])

  const headerCount = sorted.length

  return (
    <div style={styles.container}>
      {selectedJob && (
        <JobDetailErrorBoundary key={selectedJob.id} job={selectedJob} onClose={() => setSelectedJob(null)}>
          <JobCard job={selectedJob} onClose={() => setSelectedJob(null)} onUpdate={() => { fetchJobs(); setSelectedJob(null) }} />
        </JobDetailErrorBoundary>
      )}
      {showNewJob && <NewJobModal defaultDate={new Date().toISOString().split('T')[0]} activeBusiness={activeBusiness !== 'all' ? activeBusiness : 'pools'} onClose={() => setShowNewJob(false)} onSave={() => { fetchJobs(); setShowNewJob(false) }} />}

      <div style={styles.header}>
        <div>
          <div style={styles.title}>JOBS</div>
          <div style={styles.subtitle}>
            {headerCount} {tab === 'active' ? 'active' : 'completed'}
            {savingOrder && <span style={styles.savingTag}> · saving order…</span>}
          </div>
        </div>
        {canEditJobs && <button onClick={() => setShowNewJob(true)} style={styles.addBtn}>+ NEW JOB</button>}
      </div>

      <div style={styles.tabs}>
        <button onClick={() => setTab('active')} style={{ ...styles.tabBtn, ...(tab === 'active' ? styles.tabBtnActive : {}) }}>
          ACTIVE <span style={styles.tabCount}>{activeJobs.length}</span>
        </button>
        <button onClick={() => setTab('completed')} style={{ ...styles.tabBtn, ...(tab === 'completed' ? styles.tabBtnActive : {}) }}>
          COMPLETED <span style={styles.tabCount}>{completedJobs.length}</span>
        </button>
        <button onClick={() => setTab('cancelled')} style={{ ...styles.tabBtn, ...(tab === 'cancelled' ? styles.tabBtnActive : {}) }}>
          CANCELLED <span style={styles.tabCount}>{cancelledJobs.length}</span>
        </button>
      </div>

      <div style={styles.toolbar}>
        <input value={search} onChange={e => setSearch(e.target.value)} style={styles.search} placeholder="Search by name, address, phone, status..." />
        {tab === 'active' && (
          <div style={styles.filters}>
            {[{ k: 'today', l: 'TODAY' }, { k: 'week', l: 'THIS WEEK' }, { k: 'all', l: 'ALL' }].map(f => (
              <button key={f.k} onClick={() => setDateScope(f.k)} style={{ ...styles.filterBtn, ...(dateScope === f.k ? styles.filterBtnActive : {}) }}>
                {f.l}
              </button>
            ))}
          </div>
        )}
        {tab === 'completed' && (
          <div style={styles.filters}>
            {[{ k: 'week', l: 'THIS WEEK' }, { k: 'month', l: 'THIS MONTH' }, { k: 'season', l: 'SEASON (YTD)' }, { k: 'all', l: 'ALL TIME' }].map(f => (
              <button key={f.k} onClick={() => setCompletedScope(f.k)} style={{ ...styles.filterBtn, ...(completedScope === f.k ? styles.filterBtnActive : {}) }}>
                {f.l}
              </button>
            ))}
          </div>
        )}
        {tab === 'cancelled' && (
          <div style={styles.filters}>
            {[{ k: 'week', l: 'THIS WEEK' }, { k: 'month', l: 'THIS MONTH' }, { k: 'season', l: 'SEASON (YTD)' }, { k: 'all', l: 'ALL TIME' }].map(f => (
              <button key={f.k} onClick={() => setCancelledScope(f.k)} style={{ ...styles.filterBtn, ...(cancelledScope === f.k ? styles.filterBtnActive : {}) }}>
                {f.l}
              </button>
            ))}
          </div>
        )}
        {canEditJobs && tab === 'active' && (
          <div style={styles.sortWrap}>
            <label style={styles.sortLabel}>SORT</label>
            <select value={sortMode} onChange={e => setSortMode(e.target.value)} style={styles.sortSelect}>
              <option value="priority">Priority</option>
              <option value="date">Date</option>
              <option value="customer">Customer</option>
            </select>
          </div>
        )}
        {isOwner && tab === 'active' && (
          <button onClick={handleResetOrder} style={styles.resetBtn} title="Reset priority order to calendar date">
            ↺ RESET ORDER
          </button>
        )}
      </div>

      {tab === 'completed' && canSeeCollected && completedStats.count > 0 && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{completedStats.count}</div>
            <div style={styles.statLabel}>COMPLETED</div>
          </div>
          <div style={{ ...styles.statCard, color: '#4a8c6a' }}>
            <div style={{ ...styles.statValue, color: '#4a8c6a' }}>${Number(completedStats.total).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            <div style={styles.statLabel}>COLLECTED</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ ...styles.statValue, color: '#b48c3c' }}>${Number(completedStats.avg).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
            <div style={styles.statLabel}>AVG / JOB</div>
          </div>
          {completedStats.missing > 0 && (
            <div style={{ ...styles.statCard, color: '#e05c5c', backgroundColor: 'rgba(224,92,92,0.06)' }}>
              <div style={{ ...styles.statValue, color: '#e05c5c' }}>{completedStats.missing}</div>
              <div style={styles.statLabel}>MISSING $</div>
            </div>
          )}
        </div>
      )}

      {tab === 'cancelled' && cancelledStats.count > 0 && (
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{cancelledStats.count}</div>
            <div style={styles.statLabel}>CANCELLED</div>
          </div>
          {canSeeCollected && (
            <div style={styles.statCard}>
              <div style={{ ...styles.statValue, color: 'rgba(255,255,255,0.45)' }}>${Number(cancelledStats.lost).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
              <div style={styles.statLabel}>CONTRACT VALUE NOT RUN</div>
            </div>
          )}
        </div>
      )}

      {!canDrag && canEditJobs && tab === 'active' && (sortMode !== 'priority' || search || dateScope !== 'all') && (
        <div style={styles.dragHint}>
          Drag-to-reorder is disabled while sorted by {sortMode}, searching, or filtered. Switch sort to “Priority”, clear search, and set “All” to reorder.
        </div>
      )}

      {loading ? (
        <div style={styles.empty}>Loading jobs...</div>
      ) : sorted.length === 0 ? (
        <div style={styles.empty}>No jobs found.</div>
      ) : (
        <div style={styles.list} ref={listRef}>
          {sorted.map((job, idx) => {
            const todayIso = new Date().toISOString().slice(0, 10)
            const activeSeg = job.has_segments ? pickRelevantSegment(job.segments, todayIso) : null
            const displayStart = activeSeg?.start_date || job.start_date
            const displayEnd = activeSeg?.end_date || job.end_date
            const displayJob = activeSeg ? { ...job, start_date: displayStart, end_date: displayEnd } : job
            const acc = priorityAccent(displayJob)
            const isDragging = dragIdx === idx
            const isOver = overIdx === idx && dragIdx != null && dragIdx !== idx
            const today = isToday(displayStart) && tab === 'active'
            const rowStyle = {
              ...styles.jobRow,
              borderLeft: `4px solid ${acc.color}`,
              opacity: isDragging ? 0.4 : 1,
              transform: isOver ? 'translateY(2px)' : 'none',
              boxShadow: isOver ? '0 0 0 1px rgba(180,140,60,0.6)' : undefined,
              backgroundColor: today ? 'rgba(180,140,60,0.06)' : styles.jobRow.backgroundColor,
            }
            return (
              <div
                key={job.id}
                data-job-idx={idx}
                className={acc.pulse ? 'jobs-overdue-pulse' : undefined}
                draggable={canDrag}
                onDragStart={onDragStart(idx)}
                onDragOver={onDragOver(idx)}
                onDrop={onDrop(idx)}
                onDragEnd={onDragEndHandler}
                onClick={() => { if (dragIdx == null && !justDraggedRef.current) setSelectedJob(job) }}
                style={rowStyle}
              >
                {canDrag && (
                  <div
                    onTouchStart={onTouchStart(idx)}
                    onClick={e => e.stopPropagation()}
                    style={styles.dragHandle}
                    title="Drag to reorder (long-press on mobile)"
                  >
                    <span style={styles.gripDots}>⋮⋮</span>
                  </div>
                )}
                <div style={styles.jobRowLeft}>
                  <div style={styles.jobTopRow}>
                    <div style={styles.jobNumber}>{job.job_number || '—'}</div>
                    {activeSeg && <span style={{ ...styles.accentPill, color: '#4a8c6a', borderColor: '#4a8c6a60' }}>VISIT {activeSeg.segment_number}/{job.segments.length}</span>}
                    {today && <span style={styles.todayPill}>TODAY</span>}
                    {acc.label && !today && (
                      <span style={{ ...styles.accentPill, color: acc.color, borderColor: acc.color + '60' }}>{acc.label}</span>
                    )}
                  </div>
                  <div style={{ ...styles.jobClient, textDecoration: acc.struck ? 'line-through' : 'none', color: acc.struck ? 'rgba(255,255,255,0.4)' : styles.jobClient.color }}>{job.client_name}</div>
                  {activeSeg?.description && <div style={{ fontSize: '12px', color: 'rgba(74,140,106,0.9)', marginBottom: '2px' }}>{activeSeg.description}</div>}
                  {job.site_address && <div style={styles.jobAddress}>📍 {job.site_address}{job.site_city ? ', ' + job.site_city : ''}</div>}
                  {job.profiles?.full_name && <div style={styles.jobCrew}>👤 {job.profiles.full_name}</div>}
                </div>
                <div style={styles.jobRowRight}>
                  <div style={{ ...styles.statusBadge, backgroundColor: getStatusColor(job.status) + '20', color: getStatusColor(job.status), borderColor: getStatusColor(job.status) + '40' }}>
                    {job.status?.replace('_', ' ').toUpperCase()}
                  </div>
                  {job.status === 'completed' && canSeeCollected && (
                    job.amount_collected != null ? (
                      <div style={styles.collectedBadge}>{String.fromCodePoint(0x2713)} ${Number(job.amount_collected).toLocaleString('en-US', { maximumFractionDigits: 0 })}</div>
                    ) : (
                      <div style={styles.collectedMissing} onClick={function(e) { e.stopPropagation(); setSelectedJob(job) }}>SET COLLECTED $</div>
                    )
                  )}
                  <div style={styles.jobDates}>{formatDate(displayStart)}{displayEnd && displayEnd !== displayStart ? ` → ${formatDate(displayEnd)}` : ''}</div>
                  <div style={{ ...styles.bizTag, color: getBizColor(job.business_id) }}>{job.business_id === 'pools' ? 'CPP' : 'RBC'}</div>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

const styles = {
  container: { fontFamily: "'Barlow',sans-serif", maxWidth: '100%' },
  header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '16px' },
  title: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '28px', fontWeight: 700, letterSpacing: '3px', color: '#ffffff' },
  subtitle: { fontSize: '15px', color: 'rgba(255,255,255,0.35)', marginTop: '4px' },
  savingTag: { color: '#b48c3c', fontSize: '12px', letterSpacing: '1px', fontFamily: "'Barlow Condensed',sans-serif" },
  addBtn: { backgroundColor: '#b48c3c', border: 'none', borderRadius: '3px', padding: '12px 20px', color: '#0a0f1a', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '13px', fontWeight: 700, letterSpacing: '2px', cursor: 'pointer' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '14px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  tabBtn: { padding: '10px 16px', backgroundColor: 'transparent', border: 'none', borderBottom: '2px solid transparent', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 600, letterSpacing: '2px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' },
  tabBtnActive: { color: '#ffffff', borderBottomColor: '#b48c3c' },
  tabCount: { fontSize: '11px', backgroundColor: 'rgba(255,255,255,0.08)', padding: '2px 8px', borderRadius: '10px' },
  toolbar: { display: 'flex', gap: '10px', marginBottom: '14px', flexWrap: 'wrap', alignItems: 'center' },
  search: { flex: 1, minWidth: '200px', backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '10px 14px', color: '#ffffff', fontSize: '15px', fontFamily: "'Barlow',sans-serif", outline: 'none' },
  filters: { display: 'flex', gap: '4px', flexWrap: 'wrap' },
  filterBtn: { padding: '8px 12px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', color: 'rgba(255,255,255,0.4)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1px', cursor: 'pointer' },
  filterBtnActive: { backgroundColor: 'rgba(180,140,60,0.15)', borderColor: 'rgba(180,140,60,0.4)', color: '#b48c3c' },
  sortWrap: { display: 'flex', alignItems: 'center', gap: '6px' },
  sortLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '2px', color: 'rgba(255,255,255,0.4)' },
  sortSelect: { backgroundColor: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '3px', padding: '8px 10px', color: '#ffffff', fontSize: '13px', fontFamily: "'Barlow',sans-serif", outline: 'none', cursor: 'pointer' },
  resetBtn: { padding: '8px 14px', backgroundColor: 'transparent', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '3px', color: 'rgba(255,255,255,0.55)', fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', fontWeight: 600, letterSpacing: '1.5px', cursor: 'pointer' },
  dragHint: { padding: '10px 14px', marginBottom: '12px', backgroundColor: 'rgba(180,140,60,0.06)', border: '1px solid rgba(180,140,60,0.2)', borderRadius: '3px', color: 'rgba(180,140,60,0.85)', fontSize: '12px', fontFamily: "'Barlow',sans-serif" },
  list: { display: 'flex', flexDirection: 'column', gap: '6px' },
  jobRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '3px', cursor: 'pointer', gap: '12px', transition: 'transform 0.12s ease, box-shadow 0.12s ease, opacity 0.12s ease' },
  dragHandle: { width: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab', color: 'rgba(255,255,255,0.35)', fontSize: '20px', userSelect: 'none', touchAction: 'none', padding: '4px', marginRight: '-4px' },
  gripDots: { lineHeight: 1, letterSpacing: '-2px' },
  jobRowLeft: { flex: 1, minWidth: 0 },
  jobTopRow: { display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' },
  jobNumber: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '2px', color: 'rgba(255,255,255,0.35)' },
  accentPill: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', padding: '2px 8px', borderRadius: '2px', border: '1px solid', backgroundColor: 'rgba(0,0,0,0.2)' },
  todayPill: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', letterSpacing: '1.5px', padding: '2px 8px', borderRadius: '2px', color: '#0a0f1a', backgroundColor: '#b48c3c', fontWeight: 700 },
  jobClient: { fontSize: '17px', fontWeight: 500, color: '#ffffff', marginBottom: '4px' },
  jobAddress: { fontSize: '13px', color: 'rgba(255,255,255,0.35)', marginBottom: '2px' },
  jobCrew: { fontSize: '13px', color: 'rgba(255,255,255,0.35)' },
  jobRowRight: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px', flexShrink: 0 },
  statusBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1px', padding: '4px 10px', borderRadius: '3px', border: '1px solid' },
  jobDates: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', fontFamily: "'Barlow Condensed',sans-serif", letterSpacing: '1px' },
  bizTag: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '11px', letterSpacing: '1.5px', fontWeight: 700 },
  empty: { padding: '60px', textAlign: 'center', color: 'rgba(255,255,255,0.25)', fontSize: '15px', backgroundColor: 'rgba(255,255,255,0.02)', borderRadius: '4px', border: '1px solid rgba(255,255,255,0.05)' },
  statCard: { flex: 1, minWidth: '120px', padding: '14px 18px', backgroundColor: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '4px' },
  statValue: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '22px', fontWeight: 700, color: '#ffffff', lineHeight: 1.1 },
  statLabel: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 600, letterSpacing: '1.5px', color: 'rgba(255,255,255,0.4)', marginTop: '4px' },
  collectedBadge: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', fontWeight: 700, letterSpacing: '1px', color: '#4a8c6a', backgroundColor: 'rgba(74,140,106,0.12)', border: '1px solid rgba(74,140,106,0.35)', padding: '3px 9px', borderRadius: '3px' },
  collectedMissing: { fontFamily: "'Barlow Condensed',sans-serif", fontSize: '10px', fontWeight: 700, letterSpacing: '1.5px', color: '#b48c3c', backgroundColor: 'rgba(180,140,60,0.1)', border: '1px dashed rgba(180,140,60,0.4)', padding: '3px 9px', borderRadius: '3px', cursor: 'pointer' },
}

// Silence unused import warning for ACTIVE_STATUSES (referenced via utility only)
void ACTIVE_STATUSES
